import { useCallback, useEffect, useState } from 'react';
import {
  admin,
  ApiError,
  type Category,
  type Site,
  type SiteDetail,
} from '../lib/api.ts';
import { CodeCard } from './CodeCard.tsx';
import { InstallCard } from './InstallCard.tsx';
import { NodeUsage } from './NodeUsage.tsx';
import { Supervisors } from './Supervisors.tsx';
import { WeeklyHours } from './WeeklyHours.tsx';
import { Categories } from './Categories.tsx';
import { Settings } from './Settings.tsx';

interface Props {
  readonly sites: readonly Site[];
  readonly categories: readonly Category[];
  /** Passed to People, where only the owner may hand over. */
  readonly meId?: string | null;
  readonly onSitesChanged: () => void;
  /** Which pane, chosen in the sidebar rather than in here. */
  readonly pane?: 'sites' | 'people' | 'cats' | 'brand';
}

/**
 * Rollout screen. One site at a time, because that is how a rollout
 * actually happens -- a technician is standing at one counter.
 */
const ADMIN_ERRORS: Record<string, string> = {
  site_limit_reached: 'This plan has no more site slots.',
  device_limit_reached:
    'No free device slots at this site. Revoke one first.',
  not_found: 'That item no longer exists. Refresh and try again.',
  admin_only: 'Only an administrator can do that.',
  bad_request: 'Some details were not valid.',
};

export const Admin = (
  { sites, categories, onSitesChanged, meId, pane = 'sites' }: Props,
) => {
  const [siteId, setSiteId] = useState('');
  const [detail, setDetail] = useState<SiteDetail | null>(null);
  const [deviceName, setDeviceName] = useState('');
  const [newSite, setNewSite] = useState('');
  const [staffName, setStaffName] = useState('');
  const [staffPin, setStaffPin] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!siteId && sites[0]) setSiteId(sites[0].id);
  }, [sites, siteId]);

  const refresh = useCallback(() => {
    if (!siteId) return;
    admin.siteDetail(siteId).then(setDetail).catch(() => setDetail(null));
  }, [siteId]);

  useEffect(refresh, [refresh]);

  const run = async (fn: () => Promise<unknown>, after?: () => void) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      after?.();
      refresh();
    } catch (e) {
      // Show the server's reason. "Check the details" told an admin
      // nothing and hid a real bug for an entire session.
      const code = e instanceof ApiError ? e.code : '';
      setError(
        ADMIN_ERRORS[code] ??
          (code ? `That did not work (${code}).` : 'That did not work.'),
      );
    } finally {
      setBusy(false);
    }
  };

  const site = sites.find((s) => s.id === siteId);

  // Seeded from the selected site, so saving cannot write blanks over
  // real values -- the mistake that let the People editor clear a
  // person's chat-only flag.
  const [timezone, setTimezone] = useState('');

  useEffect(() => {
    setTimezone(site?.timezone ?? 'America/New_York');
  }, [site?.id, site?.timezone]);

  // Opening times moved to WeeklyHours, which owns its own save. This
  // one writes only the zone, so saving the zone cannot quietly stamp
  // a stale pair of times back over the week.
  const saveZone = () =>
    run(() => admin.updateSite(siteId, { timezone }), onSitesChanged);

  return (
    <div className="noticeWrap">
      {(pane === 'sites' || pane === 'people') && <NodeUsage />}
      {pane === 'sites' && <InstallCard />}

      {pane === 'brand' ? (
        <Settings />
      ) : pane === 'cats' ? (
        <Categories categories={categories} onChanged={onSitesChanged} />
      ) : pane === 'people' ? (
        <Supervisors
              sites={sites}
              categories={categories}
              meId={meId}
            />
      ) : (
      <>
      {error && <p className="err">{error}</p>}

      <section className="composer">
        <p className="mono">Add a site</p>
        <div className="noticeRow">
          <input
            value={newSite}
            placeholder="Site name"
            onChange={(e) => setNewSite(e.target.value)}
          />
          <button
            disabled={busy || newSite.trim().length === 0}
            onClick={() =>
              run(
                () => admin.createSite({ name: newSite.trim() }),
                () => {
                  setNewSite('');
                  onSitesChanged();
                },
              )
            }
          >
            Create
          </button>
        </div>
      </section>

      <div className="noticeRow">
        <select
          value={siteId}
          onChange={(e) => setSiteId(e.target.value)}
          aria-label="Site"
        >
          {sites.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
        {/* Just the count. A plan is bought in nodes across the
            whole account, not per site, so "1 of 3" invited someone
            to read a per-site cap as their allowance. */}
        {detail && (
          <span className="mono">
            {detail.devices.length} node
            {detail.devices.length === 1 ? '' : 's'} at this site
          </span>
        )}
      </div>

      {site && (
        <section className="composer">
          <p className="mono">Hours for {site.name}</p>
          <p className="hint">
            Notifications are held outside these hours, and chats are
            closed at the end of the trading day. A window that ends
            before it starts runs overnight.
          </p>
          <WeeklyHours siteId={site.id} timezone={timezone} />

          <div className="noticeRow">
            <label className="fieldLab">Timezone</label>
            <input
              value={timezone}
              placeholder="America/New_York"
              onChange={(e) => setTimezone(e.target.value)}
            />
            <button className="primary" disabled={busy} onClick={saveZone}>
              Save timezone
            </button>
          </div>
        </section>
      )}

      <section className="composer">
        <p className="mono">Enrol a node</p>
        <div className="noticeRow">
          <input
            value={deviceName}
            placeholder="Name it, e.g. Reception 1"
            onChange={(e) => setDeviceName(e.target.value)}
          />
          <button
            className="primary"
            disabled={busy || !site || detail?.slotsLeft === 0}
            onClick={() =>
              run(
                () => admin.issueCode(siteId, deviceName.trim() || null),
                () => setDeviceName(''),
              )
            }
          >
            Issue code
          </button>
        </div>
        {detail?.slotsLeft === 0 && (
          <p className="hint">
            This site has reached its node limit. Revoke one, or
            ask us to raise the limit for this site.
          </p>
        )}
      </section>

      {detail && detail.pendingCodes.length > 0 && (
        <section>
          <p className="mono covHead">Waiting to be entered</p>
          {detail.pendingCodes.map((c) => (
            <CodeCard key={c.id} code={c} />
          ))}
        </section>
      )}

      {detail && (
        <section>
          <p className="mono covHead">
            Enrolled nodes &mdash; {detail.devices.length}
          </p>
          {detail.devices.length === 0 ? (
            <p className="empty">No devices enrolled at this site.</p>
          ) : (
            <ul className="covList">
              {detail.devices.map((d) => {
                const mins =
                  d.minutes_since === null
                    ? null
                    : Math.round(Number(d.minutes_since));
                return (
                  <li key={d.id}>
                    <span>
                      {d.name}
                      <span className="covWhen"> v{d.app_version ?? '?'}</span>
                    </span>
                    <span className="covWhen">
                      {mins === null
                        ? 'never seen'
                        : mins < 20
                          ? 'online'
                          : `${mins}m ago`}
                      <button
                        className="linkish danger"
                        disabled={busy}
                        onClick={() => run(() => admin.revokeDevice(d.id))}
                      >
                        Revoke
                      </button>
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      )}

      <section className="composer">
        <p className="mono">Add staff PIN</p>
        <p className="hint">
          A name and four digits, so acknowledgements carry a person.
          Not a login.
        </p>
        <div className="noticeRow">
          <input
            value={staffName}
            placeholder="Full name"
            onChange={(e) => setStaffName(e.target.value)}
          />
          <input
            value={staffPin}
            placeholder="4-digit PIN"
            inputMode="numeric"
            maxLength={6}
            className="pinInput"
            onChange={(e) => setStaffPin(e.target.value.replace(/\D/g, ''))}
          />
          <button
            disabled={
              busy || !staffName.trim() || staffPin.length < 4 || !site
            }
            onClick={() =>
              run(
                () => admin.addStaff(siteId, staffName.trim(), staffPin),
                () => {
                  setStaffName('');
                  setStaffPin('');
                },
              )
            }
          >
            Add
          </button>
        </div>
      </section>
      </>
      )}
    </div>
  );
};
